import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Label
} from "recharts";
import { Category } from "../";
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';

const colors = ['#3f73f9', '#f9143a', '#5dce5a', '#eeb55d', '#8dbfea', '#a566d5', '#f99d9d', '#f4e75e', '#77797a', '#cccdcc'];

function CustomTooltip({ label, active, payload, dataNames, legend, isMobile }) {
  if (!active || !payload) return null;

  const campaignData = payload.reduce((acc, entry) => {
    const key = String(entry.dataKey ?? '');
    const u = key.lastIndexOf('_');
    const campaignName = u >= 0 ? key.slice(0, u) : key;
    const metric = u >= 0 ? key.slice(u + 1) : '';
    if (!acc[metric]) acc[metric] = {};
    acc[metric][campaignName] = entry.value;
    return acc;
  }, {});

  return (
    <Card
      variant="outlined"
      sx={{
        padding: isMobile ? 1 : 2,
        minWidth: isMobile ? 180 : 240,
        maxWidth: isMobile ? 220 : 320,
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? 4 : 10,
      }}
    >
      <Typography variant="subtitle2" gutterBottom style={{ fontSize: isMobile ? 12 : 16 }}>
        {label}
      </Typography>
      {legend.map(metric => (
        <div key={metric.key}>
          <Typography variant="body2" style={{ fontSize: isMobile ? 11 : 14 }}>
            {metric.name}
          </Typography>
          {dataNames?.map((name, index) => (
            <div key={name} style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 4 : 8 }}>
              <div style={{
                width: isMobile ? 8 : 10,
                height: isMobile ? 8 : 10,
                backgroundColor: colors[index % colors.length]
              }}/>
              <Typography variant="body2" style={{ fontSize: isMobile ? 11 : 14 }}>
                {name}: {new Intl.NumberFormat('ru-RU').format(campaignData[metric.key]?.[name] || 0)}
              </Typography>
            </div>
          ))}
        </div>
      ))}
    </Card>
  );
}

function RechartsDashed({ type, legend, data, userData, isMobile }) {
  const normalise_data = [];
  if (userData.user.id === "84149b79-02d9-4919-8a92-e2531594dc4b") {
    data.names = ["walexoil.com"]
  }
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const [datePart] = dateStr.split('T');
      switch(type) {
        case 'hour':
          return datePart.slice(-8, -6) + ':' + datePart.slice(-5, -3);
        case 'week':
          return datePart.slice(8, 10) + '/' + datePart.slice(5, 7);
        default:
          return datePart.slice(8, 10) + '/' + datePart.slice(5, 7);
      }
    } catch {
      return dateStr;
    }
  };

  if (data?.names && data.data && data.time_intervals) {
    data.time_intervals.forEach((interval, intervalIndex) => {
      const entry = { date: formatDate(interval[0]) };

      data.names.forEach((campaignName, campaignIndex) => {
        data.metrics.forEach((metric, metricIndex) => {
          entry[`${campaignName}_${metric}`] = 
            data.data[campaignIndex]?.[intervalIndex]?.[metricIndex] || 0;
        });
      });

      normalise_data.push(entry);
    });
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        data={normalise_data}
        margin={{ top: 30, right: 20, left: 20, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="date" />
        <YAxis yAxisId="left" />
        <YAxis yAxisId="right" orientation="right" />
        
        <Tooltip 
          content={<CustomTooltip 
            dataNames={data?.names} 
            legend={legend}
            isMobile={isMobile}
          />}
        />

        {data?.names?.map((campaignName, campaignIndex) => 
          legend.map((metric, metricIndex) => (
            <Line
              key={`${campaignName}_${metric.key}`}
              yAxisId={metricIndex % 2 === 0 ? "left" : "right"}
              dataKey={`${campaignName}_${metric.key}`}
              stroke={colors[campaignIndex % colors.length]}
              dot={false}
            />
          ))
        )}
      </LineChart>
    </ResponsiveContainer>
  );
}

export default RechartsDashed;